import {
  select,
  call,
  put,
  cancelled,
  take,
  fork,
  cps,
  delay
} from "redux-saga/effects";
import { eventChannel, buffers } from "redux-saga";
import hash from "object-hash";
import * as firebase from "firebase/app";
import { getFirebaseRef } from "utils/firebase";
import * as firebaseSelectors from "selectors/firebase";
import { FIREBASE_READY } from "constants/actionTypes";
import {
  setFirebaseErrorStatus,
  setFirebaseLastSuccessfulWrite,
  addPendingWrite,
  removePendingWrite,
  updateFirebaseMetadata
} from "actions/firebase";
import { stripNullKeys } from "utils";
// These need to be imported in order to allow for the auth()
// and database() functions in the new Firebase sdk
import "firebase/auth";
import "firebase/database";

/**
 * This saga will handle creating new child entries in Firebase with an auto-
 * generated ID. The ID is also set in the passed data under the `id` key. It
 * will return the generated ID.
 *
 * @param {Object} updates  An object mapping paths in Firebase to data to set
 *                          (under an auto-generated ID).
 *
 * For example, the update object
 *
 *   {
 *     '/first/path': { test: true },
 *     '/second/path': { foo: 'bar' }
 *   }
 *
 * will result in the following being written to Firebase (assuming the
 * auto-generated ID '-JyPBJNtNTKmZQoE1NKW':
 *
 *   {
 *     '/first/path/-JyPBJNtNTKmZQoE1NKW': { test: true, id: '-JyPBJNtNTKmZQoE1NKW' },
 *     '/second/path/-JyPBJNtNTKmZQoE1NKW': { foo: 'bar', id: '-JyPBJNtNTKmZQoE1NKW' }
 *   }
 */
export function* push(updates = {}) {
  const paths = Object.keys(updates);
  if (paths.length > 0) {
    const rootRef = yield call(ref, "/");
    // Push generates an ID and returns a ref to the new location. We can get
    // the generated ID by getting the key of the ref. Merge this ID into the
    // data we're storing.
    const baseRef = yield call([rootRef, rootRef.child], paths[0]);
    const newRef = yield call([baseRef, baseRef.push]);
    const key = newRef.key;
    yield call(
      [rootRef, rootRef.update],
      paths.reduce((memo, path) => {
        // Build object of paths with new key/ID appended mapped to data.
        memo[`${path}/${key}`] = { ...updates[path], id: key };
        return memo;
      }, {})
    );
    return key;
  }
  return undefined;
}

/**
 * Removes the data at the specified paths by setting each path to null in one
 * atomic update.
 *
 * @param {Array} paths  List of paths referencing data to delete.
 */
export function* remove(paths = []) {
  if (paths.length > 0) {
    const rootRef = yield call(ref, "/");
    // Set each path to null and run one multi-location update.
    yield call(
      [rootRef, rootRef.update],
      paths.reduce((memo, path) => {
        return { ...memo, [path]: null };
      }, {})
    );
  }
}

/**
 * Performs a raw Firebase `set` operation at the specified path
 * (i.e. no error handling)
 *
 * @param path
 * @param value
 * @returns {IterableIterator<any>}
 */

export function* firebaseSet(path, value) {
  const pathRef = yield call(ref, path);
  yield cps([pathRef, pathRef.set], value);
}

/**
 * Sets the passed data at the path.
 *
 * @param {String} path          [description]
 * @param {Any}    value         [description]
 */
export function* set(path, value) {
  // Get the ref for the path and call set there.
  const pathRef = yield call(ref, path);

  // create a hash of the new data after stripping null (ex. { foo: null }) keys.
  // firebase will be stripping on its side, so if we don't do this, the hashes
  // we're storing here won't match some that come back, which would be pointless!
  const hashedValue = hash(stripNullKeys(value));

  try {
    yield put(addPendingWrite(path, hashedValue));
    yield cps([pathRef, pathRef.set], value);

    if (yield select(state => state.firebase.error)) {
      yield delay(3000);
      yield put(setFirebaseErrorStatus(undefined));
    }
  } catch (e) {
    yield put(setFirebaseErrorStatus(e.message.toString()));
    yield put(removePendingWrite(path, hashedValue));
  }
}

/**
 * Uses a redux-saga eventChannel (with a sliding buffer of size so that we only
 * ever have the latest) to handle new `value` events from a Firebase ref. It
 * calls the specified callback with the most recent value it has received
 * from Firebase. It will call the callback with all the data at the location.
 *
 * @param {String}   path      The path in Firebase to sync.
 * @param {Function} callback  The function to call upon new `value` events.
 * @param {*}        args      Arguments to be sent to the callback function
 */
export function* sync(path, callback, args) {
  // Get ref and build an event channel to listen for firebase 'value' events
  // at the specified path.
  const firebaseRef = yield call(ref, path);
  const firebaseEventChannel = yield call(
    createFirebaseEventChannel,
    firebaseRef,
    ["value"]
  );

  try {
    while (true) {
      const res = yield take(firebaseEventChannel);
      yield call(callback, res.data, args);
    }
  } finally {
    if (yield cancelled()) {
      firebaseEventChannel.close();
    }
  }
}

/**
 * Uses a redux-saga eventChannel to handle new `child_changed` and
 * `child_removed` events from a Firebase ref. We use an expanding buffer so
 * that we don't drop any events, since that could mean we miss something. It
 * calls the callback with an object of the form:
 *   - key:   The key/id of the child that was changed or removed.
 *   - data:  The data of the changed child, or null if the child was removed.
 *
 * @param {String}   path           The path in Firebase to sync.
 * @param {Function} pathCallback   A function to replace the entire path's reducer
 * @param {Function} childCallback  A function to replace a single child in the path's reducer.
 * @param {Object}   args           Arguments to be sent to the callback functions
 */
export function* syncChildren(path, pathCallback, childCallback, args) {
  // Get ref and build an event channel to listen for firebase `child_changed`
  // and `child_removed` events at the specified path.
  const firebaseRef = yield call(ref, path);
  const buffer = yield call(buffers.expanding, 10);
  const firebaseEventChannel = yield call(
    createFirebaseEventChannel,
    firebaseRef,
    ["child_added", "child_changed", "child_removed", "value"],
    buffer
  );

  // Firebase guarantees that all `child_added` events will fire before the first `value` event.
  // Take advantage of that by disregarding all `child_added` events until the first `value` arrives.
  // When it does, pre-populate the entire reducer with one action instead of forking for each child.
  // Ignore all future `value` events and fallback to standard child-event behaviour.
  let hasValueReturned = false;

  try {
    while (true) {
      const res = yield take(firebaseEventChannel);

      if (hasValueReturned && res.event !== "value") {
        const writePath = `${path}/${res.key}`;

        const allPendingWrites = yield select(
          firebaseSelectors.selectPendingWrites
        );
        const pendingWrites = allPendingWrites[writePath] || [];
        const hashedData = pendingWrites.length ? hash(res.data) : null;

        if (!hashedData || !pendingWrites.includes(hashedData)) {
          // Since we want to handle each event in the buffer quickly, run the
          // callback in a non-blocking manner. We transform the data from the
          // `child_removed` event to be null, since Firebase actually gives
          // you the old data snapshot in that case.
          const data = res.event === "child_removed" ? null : res.data;
          yield fork(childCallback, { key: res.key, data }, args);
        } else {
          // this is the pendingWrites/hit case, meaning we originated this update.
          // don't bother calling callback (REPLACE), we already have the data.
          // just record that it came back successfully by removing from `pending`

          yield put(setFirebaseLastSuccessfulWrite(Date.now()));

          if (hashedData) {
            yield put(removePendingWrite(writePath, hashedData));
          }
        }
      } else if (!hasValueReturned && res.event === "value") {
        yield fork(pathCallback, { data: res.data }, args);
        hasValueReturned = true;
      }
    }
  } finally {
    if (yield cancelled()) {
      firebaseEventChannel.close();
    }
  }
}

/**
 * Waits for Firebase to be ready in state before yielding/resolving.
 */
export function* waitReady() {
  if (!(yield call(getFirebaseApp))) {
    yield take(FIREBASE_READY);
  }
}

/**
 * Returns a Firebase ref to the specified path.
 *
 * @param {String} path  The path to get a ref for.
 */
export function* ref(path) {
  const firebaseApp = yield call(getFirebaseApp);
  return yield call(getFirebaseRef, firebaseApp, path);
}

// Helpers

/**
 * Gets or creates and sets a Firebase App reference, to be stored in the
 * firebase reducer
 *
 * @returns {*}
 */

export function* getFirebaseApp() {
  if (!firebase.apps.length) {
    /* global FIREBASE_API_KEY, FIREBASE_NAMESPACE, FIREBASE_PROJECT_ID */
    const config = {
      apiKey: FIREBASE_API_KEY,
      authDomain: `${FIREBASE_NAMESPACE}.firebaseapp.com`,
      databaseURL: `https://${FIREBASE_NAMESPACE}.firebaseio.com`,
      projectId: FIREBASE_PROJECT_ID
    };

    firebase.initializeApp(config);

    yield put(
      updateFirebaseMetadata({
        app: firebase
      })
    );
  }

  return firebase;
}

export function createFirebaseEventChannel(
  firebaseRef,
  events,
  buffer = buffers.sliding(1)
) {
  // We're defaulting to a sliding buffer of size 1 to protect against the
  // chance of missing an event. If the callback is still executing
  // when a new event comes in, then the TAKE effect will miss the event. If
  // we add a sliding buffer of size 1, we'll always only buffer the latest
  // event if one arrives while we're still executing the callback.
  return eventChannel(emit => {
    const emitData = (eventName, data) => {
      emit({
        event: eventName,
        key: data.key,
        data: data.val()
      });
    };

    const listeners = events.map(event => {
      return {
        listener: firebaseRef.on(event, emitData.bind(null, event)),
        event
      };
    });

    return () => {
      listeners.forEach(({ listener, event }) => {
        firebaseRef.off(event, listener);
      });
    };
  }, buffer);
}

export function onceValue(firebaseRef) {
  return new Promise((resolve, reject) => {
    firebaseRef.once(
      "value",
      snapshot => resolve(snapshot.val()),
      error => reject(error)
    );
  });
}

/**
 * Update paths with values
 *
 * @param {Any}    updates
 *
 * an object mapping that represents updates where the key is the path
 * and the value is the value that will be updated at that path.
 *
 * For example,, the update object
 *
 * {
 *  "/accounts/1234/users/4568": { "status": "enabled" }
 * }
 *
 * will update the user at that path with a status of enabled.
 */
export function* update(updates = {}) {
  if (Object.keys(updates).length < 1) {
    return undefined;
  }

  try {
    return yield applyUpdates(updates);
  } catch (e) {
    console.error(e);
    yield put(setFirebaseErrorStatus(e.message.toString()));
  }

  return undefined;
}

export function* applyUpdates(updates = {}) {
  const rootRef = yield call(ref, "/");
  return yield call([rootRef, rootRef.update], updates);
}

export function getTimestamp() {
  return firebase.database.ServerValue.TIMESTAMP;
}
